import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { getReadIds, markAsRead as persistMarkAsRead, setUnreadCount, isRead } from '../../utils/notificationState';
import { CONTACT_IF_QUESTIONS_NOTICE } from '../../utils/asyncConsultCopy';
import styles from './NotificationsPage.module.css';

const DUMMY_NOTIFICATIONS = [
  { id: 1, type: 'order', title: 'Order Shipped', message: 'Your order OV-10078 for Testosterone Cypionate has been shipped. Tracking number: 1Z999AA10123456784.', status: 'unread', createdAt: '2026-02-12T14:30:00Z' },
  { id: 2, type: 'prescription', title: 'Prescription Approved', message: 'Your prescription for Compounded Semaglutide has been approved by Dr. Martinez. Your order is now being processed.', status: 'unread', createdAt: '2026-02-11T09:15:00Z' },
  { id: 3, type: 'order', title: 'Refill Reminder', message: 'Your Tadalafil prescription is due for a refill in 5 days. Reorder now to avoid any gaps in your treatment.', status: 'action_required', createdAt: '2026-02-10T16:00:00Z' },
  { id: 4, type: 'doctor', title: 'Provider Message', message: 'Dr. Williams has sent you a follow-up message regarding your recent lab results. Please review at your earliest convenience.', status: 'unread', createdAt: '2026-02-09T11:45:00Z' },
  { id: 5, type: 'order', title: 'Order Delivered', message: 'Your order OV-10042 for Compounded Semaglutide has been delivered successfully.', status: 'read', createdAt: '2026-02-08T10:20:00Z' },
  { id: 6, type: 'lab', title: 'Lab Results Ready', message: `Your testosterone panel results are now available. ${CONTACT_IF_QUESTIONS_NOTICE}`, status: 'read', createdAt: '2026-02-06T08:30:00Z' },
  { id: 7, type: 'tip', title: 'Health Tip: Hydration', message: 'Staying well-hydrated can improve the effectiveness of your weight management medication. Aim for 8 glasses of water daily.', status: 'read', createdAt: '2026-02-04T12:00:00Z' },
  { id: 8, type: 'prescription', title: 'Prescription Renewal', message: 'Your Wegovy prescription has been renewed for another 90 days. No action needed on your part.', status: 'read', createdAt: '2026-02-01T15:30:00Z' },
  { id: 9, type: 'tip', title: 'Welcome to Oval Care', message: 'Welcome to Oval Care! We\'re here to support your health journey with personalized telehealth services. Browse our treatments, complete your intake form, and get started with affordable, convenient care delivered to your door.', status: 'read', createdAt: '2026-01-15T10:00:00Z' },
];

const NotificationsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  // Card 53595: a failed load used to leave `notifications` at [] and fall straight
  // through to the page's empty state, so "your feed could not be loaded" and "you
  // have no notifications" rendered identically. These two flags separate them.
  // `systemError` is the PARTIAL case: support-derived rows still render, so the list
  // looks complete while user_notifications rows are silently missing.
  const [loadError, setLoadError] = useState(false);
  const [systemError, setSystemError] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);

  // Fetch real notifications from support tickets
  const fetchNotifications = useCallback(async () => {
    setLoadError(false);
    setSystemError(false);
    try {
      const response = await api.get('/support/tickets');
      const tickets = response.tickets || [];
      const notifs = [];

      // Fetch details for recent tickets (up to 10) to get messages
      const recentTickets = tickets.slice(0, 10);
      const detailPromises = recentTickets.map(t =>
        api.get('/support/tickets/' + t.id).then(r => r.ticket).catch(() => t)
      );
      const detailedTickets = await Promise.all(detailPromises);

      detailedTickets.forEach(ticket => {
        // Add notification for each admin reply
        const messages = ticket.messages || [];
        messages.forEach(msg => {
          if (msg.senderType === 'admin') {
            notifs.push({
              id: msg.id,
              // Card 53580: a support MESSAGE id, NOT a user_notifications row -- so its
              // read-state belongs in user_notification_reads, keyed. See markAsRead below.
              source: 'key',
              type: 'support',
              title: 'Support Reply: ' + (ticket.subject || 'Ticket'),
              message: msg.content,
              ticketId: ticket.id,
              ticketNumber: ticket.ticketNumber,
              status: 'unread',
              createdAt: msg.createdAt
            });
          }
        });
        // Add notification for status changes
        if (ticket.status === 'resolved' || ticket.status === 'closed') {
          notifs.push({
            id: 'status-' + ticket.id,
            // Card 53580: a SYNTHESISED id, not even a uuid -- this is the kind that 400s
            // at PATCH /:id/read. Keyed read-state, same as the message ids above.
            source: 'key',
            type: 'support',
            title: 'Ticket ' + (ticket.status === 'resolved' ? 'Resolved' : 'Closed'),
            message: 'Your support ticket "' + ticket.subject + '" (' + ticket.ticketNumber + ') has been ' + ticket.status + '.',
            ticketId: ticket.id,
            ticketNumber: ticket.ticketNumber,
            status: 'unread',
            createdAt: ticket.updatedAt
          });
        }
      });
      // Fetch system notifications from user_notifications table
      try {
        const sysResponse = await api.get('/notifications');
        const sysNotifs = (sysResponse.notifications || []).map(n => ({
          id: n.id,
          // Card 53580: a REAL user_notifications row -- it owns `is_read`, so it keeps
          // using PATCH /:id/read (the 200 path) and must NOT be routed to read-keys.
          source: 'row',
          type: n.type || 'system',
          title: n.title,
          message: n.message,
          status: n.is_read ? 'read' : 'unread',
          createdAt: n.created_at,
          metadata: n.metadata
        }));
        notifs.push(...sysNotifs);
    } catch (e) {
      console.error('Failed to fetch system notifications:', e);
      setSystemError(true);
    }

      // Sort by date descending
      notifs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      // Apply persisted read status.
      // Card 53580: the two support-derived id kinds are not user_notifications rows, so
      // before migration 077 their ONLY store was localStorage -- i.e. "Read" reset on every
      // other device. Server keys are now the durable source; localStorage is still merged
      // ON PURPOSE so nobody loses a read-mark they made on this device before 53580.
      const fetchServerReadKeys = async () => {
        try {
          const keyResponse = await api.get('/notifications/read-keys');
          return keyResponse.keys || [];
        } catch (e) {
          console.error('Failed to fetch notification read keys:', e);
          return [];
        }
      };
      const readIds = new Set([...getReadIds(), ...(await fetchServerReadKeys())]);
      const withReadStatus = notifs.map(n => ({
        ...n,
        status: n.status === 'read' ? 'read' : (readIds.has(n.id) ? 'read' : 'unread')
      }));
      setNotifications(withReadStatus);
      // Update shared unread count
      const unreadCount = withReadStatus.filter(n => n.status === 'unread').length;
      setUnreadCount(unreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // Card 40755 — DrCare's consultation is asynchronous, so an appointment is no longer a
  // standing part of a member's notification stream and does not earn a permanent filter.
  // Scheduling still exists for the two conditional paths (a DoctorNow product that requires
  // an appointment, and a refill the server marks `schedulingRequired`), so any existing
  // `type: 'appointment'` row is still listed and still opens under All — the filter only ever
  // hid rows, it never owned them. Read state is keyed by notification id in
  // `utils/notificationState.js` and is untouched by this change.
  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'order', label: 'Order' },
    { id: 'prescription', label: 'Prescription' },
    { id: 'doctor', label: 'Doctor' },
    { id: 'lab', label: 'Lab Results' },
    { id: 'tip', label: 'Health Tip' },
    { id: 'support', label: 'Support' }
  ];

  const filteredNotifications = notifications.filter(notif => {
    if (activeTab !== 'all' && notif.type !== activeTab) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        notif.title?.toLowerCase().includes(query) ||
        notif.message?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      unread: { label: 'Unread', color: '#3b82f6', bg: '#dbeafe' },
      read: { label: 'Read', color: '#6b7280', bg: '#f3f4f6' },
      action_required: { label: 'Action Required', color: '#f59e0b', bg: '#fef3c7' }
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  // Card 53580: send the read-receipt to the store that can actually hold it.
  // `row`  -> a real user_notifications row, which owns `is_read`: PATCH /:id/read, the
  //           measured 200 path, deliberately unchanged.
  // `key`  -> a support message id, or the synthesised 'status-<ticketId>'. Neither is a row
  //           in that table, and sending them to PATCH is exactly what produced 11x404 +
  //           5x400 -- all silently swallowed by the old `.catch(() => {})`, which is why
  //           this survived so long. They go to the keyed store from migration 077.
  // The failure is LOGGED now rather than discarded: a swallowed error was the camouflage.
  const markAsRead = (notif) => {
    const { id, source } = notif;
    persistMarkAsRead(id);
    const request = source === 'row'
      ? api.patch(`/notifications/${id}/read`)
      : source === 'key'
        ? api.post('/notifications/read-keys', { key: id })
        : null;
    request?.catch(e => console.error('Failed to persist notification read state:', e));
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, status: 'read' } : n);
      const unreadCount = updated.filter(n => n.status === 'unread').length;
      setUnreadCount(unreadCount);
      return updated;
    });
  };

  const handleView = (notif) => {
    markAsRead(notif);
    setSelectedNotif(notif);
    setReplyText('');
    setReplySuccess(false);
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedNotif?.ticketId) return;
    setReplySending(true);
    try {
      await api.post('/support/tickets/' + selectedNotif.ticketId + '/reply', { message: replyText.trim() });
      setReplySuccess(true);
      setReplyText('');
    } catch (err) {
      alert('Failed to send reply: ' + (err.response?.data?.message || err.message));
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Your Notifications</h1>
        <p>Get alerts on prescriptions, orders, and pharmacy updates. Manage your health with reminders and important info all in one place.</p>
      </div>

      {selectedNotif && (
        <div className={styles.modalOverlay} onClick={() => setSelectedNotif(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{selectedNotif.title}</h3>
              <button className={styles.modalClose} onClick={() => setSelectedNotif(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalMeta}>
                <span className={styles.modalDate}>{new Date(selectedNotif.createdAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span className={styles.statusBadge} style={{ color: getStatusBadge(selectedNotif.status).color, backgroundColor: getStatusBadge(selectedNotif.status).bg }}>
                  {getStatusBadge(selectedNotif.status).label}
                </span>
              </div>
              <p className={styles.modalMessage}>{selectedNotif.message}</p>
              {selectedNotif.ticketId && (
                <div className={styles.replySection}>
                  {replySuccess ? (
                    <p className={styles.replySuccessMsg}>Reply sent successfully!</p>
                  ) : (
                    <>
                      <textarea
                        className={styles.replyTextarea}
                        placeholder="Type your reply..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={3}
                      />
                      <button
                        className={styles.replyBtn}
                        onClick={handleReply}
                        disabled={replySending || !replyText.trim()}
                      >
                        {replySending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.tabsRow}>
          <div className={styles.tabs}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.searchBox}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        <div className={styles.tableScroll}><div className={styles.table}>
          <div className={styles.tableHead}>
            <span>No.</span>
            <span>Title</span>
            <span>Message</span>
            <span>Date</span>
            <span>Status</span>
            <span>Actions</span>
          </div>

          <div className={styles.tableBody}>
            {loadError ? (
              <div className={styles.emptyRow} role="alert">
                Your notifications could not be loaded, so nothing is shown here. This is not
                the same as having none.
                <div>
                  <button type="button" className={styles.replyBtn} onClick={fetchNotifications}>
                    Try again
                  </button>
                </div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <>
                {systemError && (
                  <div className={styles.emptyRow} role="alert" style={{ padding: '1rem' }}>
                    Some system notifications could not be loaded, so this list may be incomplete.
                    <button type="button" className={styles.replyBtn} onClick={fetchNotifications}>
                      Try again
                    </button>
                  </div>
                )}
                <div className={styles.emptyRow}>
                  No notifications found.
                </div>
              </>
            ) : (
              <>
                {systemError && (
                  <div className={styles.emptyRow} role="alert" style={{ padding: '1rem' }}>
                    Some system notifications could not be loaded, so this list may be incomplete.
                    <button type="button" className={styles.replyBtn} onClick={fetchNotifications}>
                      Try again
                    </button>
                  </div>
                )}
                {filteredNotifications.map((notif, index) => {
                const status = getStatusBadge(notif.status);
                return (
                  <div key={notif.id} className={`${styles.tableRow} ${notif.status === 'unread' ? styles.unreadRow : ''}`} onClick={() => handleView(notif)} style={{cursor:'pointer'}}>
                    <span className={styles.notifNumber}>{index + 1}</span>
                    <span className={styles.notifTitle}>{notif.title}</span>
                    <span className={styles.notifMessage}>{notif.message}</span>
                    <span className={styles.notifDate}>
                      {new Date(notif.createdAt).toLocaleDateString()}
                    </span>
                    <span>
                      <span
                        className={styles.statusBadge}
                        style={{ color: status.color, backgroundColor: status.bg }}
                      >
                        {status.label}
                      </span>
                    </span>
                    <span className={styles.actions}>
                      <button onClick={() => handleView(notif)} className={styles.viewBtn} aria-label="View notification">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      </button>
                    </span>
                  </div>
                );
              })}
              </>
            )}
          </div>
        </div></div>
      </div>
    </div>
  );
};

export default NotificationsPage;
